/**
 * Share Watch Modal - Modal para compartir el link de smartwatch
 * Genera automáticamente un token de auto-login al abrirse
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Watch, Copy, Check, X, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../services/api';

const ShareWatchModal = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [watchUrl, setWatchUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [error, setError] = useState(null);
  
  // Generate token automatically when modal opens
  useEffect(() => {
    if (isOpen) {
      generateWatchToken();
    }
  }, [isOpen]);
  
  const generateWatchToken = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const response = await api.post('/auth/watch-token');
      
      if (response.data && response.data.watch_url) {
        // Use the URL from the response, but replace the domain with current origin
        // This ensures the URL works in development and production
        const token = response.data.token;
        const currentOrigin = window.location.origin;
        const generatedUrl = `${currentOrigin}/watch?t=${token}`;
        
        setWatchUrl(generatedUrl);
        setTokenExpiry(new Date(response.data.expires_at));
      }
    } catch (err) {
      console.error('Error generating watch token:', err);
      setError('Error al generar el link. Intenta de nuevo.');
      // Fallback to basic URL without token
      setWatchUrl(`${window.location.origin}/watch`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(watchUrl);
      setCopied(true);
      toast.success('¡Link con auto-login copiado!');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };
  
  const handleShare = (platform) => {
    const text = '⌚ Abre Clipper en tu smartwatch - auto-login incluido:';
    const fullMessage = `${text}\n${watchUrl}`;
    
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(fullMessage)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(watchUrl)}&text=${encodeURIComponent(text)}`,
      sms: `sms:?body=${encodeURIComponent(fullMessage)}`,
      email: `mailto:?subject=${encodeURIComponent('Clipper para tu Smartwatch')}&body=${encodeURIComponent(fullMessage)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank');
    }
    onClose();
  };
  
  // Format remaining time
  const getExpiryText = () => {
    if (!tokenExpiry) return '';
    const now = new Date();
    const hoursLeft = Math.round((tokenExpiry - now) / (1000 * 60 * 60));
    if (hoursLeft > 1) {
      return `válido por ${hoursLeft} horas`;
    }
    return 'válido por 1 hora';
  };

  if (!isOpen) return null;
  
  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Watch className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Clipper Watch</h3>
              <p className="text-xs text-muted-foreground">
                {isGenerating ? 'Generando link...' : 'Link con auto-login'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {/* URL Display */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-muted-foreground">Link para tu smartwatch:</label>
              {tokenExpiry && (
                <span className="text-xs text-green-500 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  {getExpiryText()}
                </span>
              )}
            </div>
            <div className="bg-secondary rounded-lg p-3 border border-border">
              {isGenerating ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-muted-foreground">Generando link seguro...</span>
                </div>
              ) : error ? (
                <div className="text-center py-2">
                  <p className="text-sm text-red-400 mb-2">{error}</p>
                  <Button variant="outline" size="sm" onClick={generateWatchToken}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reintentar
                  </Button>
                </div>
              ) : (
                <>
                  <code className="text-sm text-foreground break-all block mb-3 font-mono bg-background/50 p-2 rounded border border-border/50">
                    {watchUrl}
                  </code>
                  <div className="flex gap-2">
                    <Button 
                      variant={copied ? "default" : "outline"}
                      size="sm" 
                      onClick={handleCopy} 
                      className={`flex-1 ${copied ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                    >
                      {copied ? (
                        <><Check className="w-4 h-4 mr-2" /> ¡Copiado!</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-2" /> Copiar link</>
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={generateWatchToken}
                      title="Generar nuevo link"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Auto-login badge */}
          {!isGenerating && !error && watchUrl.includes('?t=') && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 text-center">
              <p className="text-xs text-green-400">
                🔐 Este link incluye auto-login - no necesitas escribir contraseña en el reloj
              </p>
            </div>
          )}
          
          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border"></div>
            <span className="text-xs text-muted-foreground">o comparte por</span>
            <div className="flex-1 h-px bg-border"></div>
          </div>
          
          {/* Share buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleShare('whatsapp')} 
              className="justify-start"
              disabled={isGenerating}
            >
              <svg className="w-4 h-4 mr-2 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleShare('telegram')} 
              className="justify-start"
              disabled={isGenerating}
            >
              <svg className="w-4 h-4 mr-2 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              Telegram
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleShare('sms')} 
              className="justify-start"
              disabled={isGenerating}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              SMS
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleShare('email')} 
              className="justify-start"
              disabled={isGenerating}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              Email
            </Button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 pt-0">
          <p className="text-xs text-muted-foreground text-center bg-secondary/50 p-2 rounded-lg">
            💡 Abre este link en Safari (Apple Watch) o el navegador de tu Wear OS
          </p>
        </div>
      </div>
    </div>
  );
  
  return createPortal(modalContent, document.body);
};

export default ShareWatchModal;
